<script setup lang="ts">
import { useIntersectionAnimation } from '@/composables/use-intersection-animation';
import type { Company } from "@/dto/company/company";

interface Props {
  companies: Company[];
  selectedCompanyId?: number;
  loading: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  'update:selectedCompanyId': [id: number | undefined];
}>();

const { elementRef } = useIntersectionAnimation();

const handleCompanyClick = (companyId: number | undefined, currentSelected: number | undefined) => {
  if (currentSelected === companyId) {
    emit('update:selectedCompanyId', undefined);
  } else {
    emit('update:selectedCompanyId', companyId);
  }
};
</script>

<template>
  <section ref="elementRef" class="relative py-8 lg:py-12 2xl:py-16 bg-warm-white">
    <div
      class="max-w-[1400px] 2xl:max-w-[1600px] 3xl:max-w-[1800px] 4xl:max-w-[2200px] mx-auto px-4 sm:px-6 lg:px-8 2xl:px-12 3xl:px-16 4xl:px-20"
    >
      <div class="fade-up">
        <!-- Section Header -->
        <div class="flex items-center justify-between mb-6 2xl:mb-8">
          <div class="flex items-center gap-3 2xl:gap-4">
            <div class="w-10 h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 rounded-xl 2xl:rounded-2xl bg-secondary/10 flex items-center justify-center">
              <Icon name="material-symbols:verified" class="w-5 h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-secondary" />
            </div>
            <div>
              <h3 class="font-secondary font-semibold text-dark-gray text-lg 2xl:text-xl 3xl:text-2xl">
                Featured Employers
              </h3>
              <p class="text-medium-gray text-sm 2xl:text-base 3xl:text-lg">
                Browse jobs from top hospitality brands
              </p>
            </div>
          </div>

          <button
            v-if="selectedCompanyId"
            @click="emit('update:selectedCompanyId', undefined)"
            class="text-sm 2xl:text-base text-primary hover:text-primary-dark font-medium transition-colors flex items-center gap-1"
          >
            <Icon name="material-symbols:close" class="w-4 h-4" />
            Clear filter
          </button>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex gap-4 overflow-x-auto pb-2">
          <div
            v-for="i in 6"
            :key="i"
            class="flex-shrink-0 w-32 h-24 2xl:w-40 2xl:h-28 bg-sand/30 rounded-2xl animate-pulse"
          ></div>
        </div>

        <!-- Company Chips -->
        <div v-else class="flex flex-wrap gap-3 2xl:gap-4">
          <button
            v-for="company in companies"
            :key="company.id"
            @click="handleCompanyClick(company.id, selectedCompanyId)"
            :class="[
              'group flex items-center gap-3 px-4 py-3 2xl:px-5 2xl:py-4 rounded-2xl 2xl:rounded-3xl border-2 transition-all duration-300',
              selectedCompanyId === company.id
                ? 'bg-primary/10 border-primary shadow-warm'
                : 'bg-white border-sand/50 hover:border-primary/30 hover:shadow-warm'
            ]"
          >
            <!-- Company Logo -->
            <div class="w-10 h-10 2xl:w-12 2xl:h-12 rounded-xl 2xl:rounded-2xl bg-warm-white overflow-hidden flex items-center justify-center">
              <BaseImage
                v-if="company.logo"
                :src="company.logo"
                :alt="company.name"
                class="w-full h-full object-contain p-1"
              />
              <Icon
                v-else
                name="material-symbols:domain"
                class="w-6 h-6 2xl:w-7 2xl:h-7 text-medium-gray"
              />
            </div>

            <!-- Company Name -->
            <span
              :class="[
                'font-medium text-sm 2xl:text-base transition-colors',
                selectedCompanyId === company.id ? 'text-primary' : 'text-dark-gray group-hover:text-primary'
              ]"
            >
              {{ company.name }}
            </span>

            <!-- Selected Indicator -->
            <Icon
              v-if="selectedCompanyId === company.id"
              name="material-symbols:check-circle"
              class="w-5 h-5 2xl:w-6 2xl:h-6 text-primary"
            />
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
